import { ChevronLeftIcon, CloseIcon, HelpIcon } from '@assets/icons';
import { IconButton } from '@components/button';
import { Drawer } from '@components/drawer';
import { Tab, TabPanel, Tabs } from '@components/tabs';
import { Typography } from '@components/typography';
import { Box, Stack, useTheme } from '@mui/material';
import { SelectionContext } from '@subsets/workspaces';
import { useContext, useState } from 'react';
import { useToggle } from 'react-use';
import { NodeInformation } from './NodeInformation';
import { ZoomController } from './ZoomController';

export const Information = () => {
  const { palette } = useTheme();
  const [value, setValue] = useState<string>('');
  const { informationDrawerOpen, toggleInformationDrawer } = useContext(SelectionContext);
  const [informationDrawerExpanded, toggleInformationDrawerExpanded] = useToggle(false);

  return (
    <Drawer
      hideBackdrop
      open={informationDrawerOpen}
      anchor="right"
      onClose={toggleInformationDrawer}
      sx={{
        '& .MuiDrawer-paper': {
          width: '80%',
          maxWidth: informationDrawerExpanded ? '50%' : '370px',
          transition: 'max-width 0.3s ease-in-out, transform 0.3s ease-in-out !important',
          backgroundColor: palette.graph.drawer.background,
          borderTop: `1px solid ${palette.background.default} !important`,
          paddingX: 4,
          overflow: 'visible',
          paddingTop: 6,
        },
      }}
      style={{}}
    >
      <Box
        position="absolute"
        top={16}
        right="100%"
        justifyContent="center"
        bgcolor={palette.graph.drawer.background}
        sx={{
          padding: 2,
          borderRadius: '4px 0 0 4px',
          border: `1px solid ${palette.grey[500]}`,
          borderRight: 'none',
        }}
      >
        <IconButton
          size={24}
          data-testid="addNodeButton"
          data-cy="Add-Node-Button"
          onClick={toggleInformationDrawer}
          Icon={informationDrawerOpen ? CloseIcon : HelpIcon}
        />
        {informationDrawerOpen && (
          <IconButton
            size={24}
            onClick={toggleInformationDrawerExpanded}
            Icon={ChevronLeftIcon}
            iconProps={{ double: true }}
            style={{ transform: informationDrawerExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          />
        )}
      </Box>
      <Box position="absolute" bottom={16} right="100%">
        <ZoomController />
      </Box>
      <Stack
        sx={{
          height: '100%',
          color: palette.text.paper,
        }}
      >
        <Tabs
          value={value}
          variant="fullWidth"
          onChange={(_, tabValue: string) => setValue(tabValue)}
        >
          <Tab value="" label="Node information" />
          <Tab value="shortcuts" label="Shortcuts" />
        </Tabs>

        <TabPanel value={value} index="" width="100%" sx={{ flex: 1, overflow: 'auto', py: 4 }}>
          <NodeInformation />
        </TabPanel>
        <TabPanel value={value} index="shortcuts" width="100%" sx={{ flex: 1, overflow: 'auto' }}>
          <Stack px={2} gap={1} mt={4}>
            <Shortcut title="Pan Canvas" description="LMB + Drag" />
            <Shortcut title="Zoom Canvas" description="Mouse Wheel" />
            <Shortcut title="Select Intersection" description="Shift + Drag" />
            <Shortcut title="Select Multiple" description="Shift + LMB" />
            <Shortcut title="Delete Selection" description="Delete" />
          </Stack>
        </TabPanel>
      </Stack>
    </Drawer>
  );
};

const Shortcut = ({ title, description }: { title: string; description: string }) => {
  const { palette } = useTheme();
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      sx={{ p: 2, '&:hover': { backgroundColor: palette.backgroundHover.default } }}
    >
      <Typography>{title}</Typography>
      <Typography variant="body2">{description}</Typography>
    </Box>
  );
};
